import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Columns3, History, Loader2 } from "lucide-react";
import { PRESETS } from "@/lib/reportTools";
import { StoreMultiPick } from "./StoreMultiPick";
import { ExportButton, TotalsRow } from "./ReportTotals";
import { DealerReportTable, isAvgCol, isIdCol } from "./SalesDealerReport";
import { LeaderboardHistory, leaderboardExport } from "./LeaderboardHistory";

const B = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
const n = (k) => (r) => r[k] ?? null;
const pct = (k, label) => ({ label, get: n(k), total: "avg", pct: true });
const sum = (k, label) => ({ label, get: n(k), total: "sum" });

export const HISTORY_REPORTS = {
  dlar: { name: "DLAR", hint: "The Sales Dealer Report from the latest DLAR in the date range (month-end DLAR for months without a daily upload)." },
  pu40: { name: "Project Under 40", hint: "The latest Project Under 40 upload in the date range.",
    cols: [{ label: "Under 40", get: n("under_40") }, sum("baseline_acts", "Baseline Acts"), sum("mtd_pace", "MTD Pace"), pct("lift_pct", "Lift %")] },
  pulse: { name: "Pulse Check", hint: "The latest Pulse Check upload in the date range.",
    cols: [pct("ta_training", "TA Training"), pct("training_pct", "Training %"), sum("merchlink_pending", "Merchlink Pending"),
      sum("b2b_remaining", "B2B Remaining"), sum("ipad_offline", "iPad Offline")] },
  avb: { name: "Activation Volume Bonus", hint: "The latest Activation Volume Bonus upload in the date range.",
    cols: [{ label: "Pacing Status", get: n("status_text") }, sum("acts", "Current Acts"), sum("pacing_acts", "Pacing Acts"),
      sum("gold", "Gold Target"), sum("platinum", "Platinum Target")] },
  leaderboard: { name: "Store Leaderboard", hint: "Rankings for the latest month in the date range. Turn on side by side to compare every month." },
};
export const STORE_COLS = (admin) => [
  ...(admin ? [{ label: "Dealer", get: n("dealer_name"), wide: true }, { label: "Address", get: n("full_address"), wide: true }] : []),
  { label: "Store ID", get: n("store_id") }, { label: "TSP ID", get: n("door_tsp") },
];
const fmt = (v, c) => (v == null || v === "" ? "—" : typeof v === "number" ? `${Math.round(v * 10) / 10}${c.pct ? "%" : ""}` : v);

const dlarCols = (d) => (d?.columns || []).map((c, i) => ({ label: c, get: (r) => r[i] ?? null,
  total: i === 0 || isIdCol(c) ? undefined : isAvgCol(c) ? "avg" : "sum" }));

const Filters = ({ f, setF, stores }) => (
  <div className="flex flex-wrap items-end gap-2" data-testid="history-filters">
    {[["today", "Today"], ["yesterday", "Yesterday"], ["this_month", "This month"], ["last_month", "Last month"]].map(([k, l]) => (
      <Button key={k} size="sm" variant={f.preset === k ? "default" : "outline"} onClick={() => { const [s, e] = PRESETS[k](); setF({ ...f, preset: k, start: s, end: e }); }} data-testid={`history-preset-${k}`}>{l}</Button>
    ))}
    <Input type="date" value={f.start} onChange={(e) => setF({ ...f, preset: "", start: e.target.value })} className="h-9 w-36 text-sm" data-testid="history-start" />
    <span className="text-xs text-slate-400 pb-2">to</span>
    <Input type="date" value={f.end} onChange={(e) => setF({ ...f, preset: "", end: e.target.value })} className="h-9 w-36 text-sm" data-testid="history-end" />
    <StoreMultiPick stores={stores} value={f.tsps} onChange={(tsps) => setF({ ...f, tsps })} />
    {f.report === "leaderboard" && (
      <Button size="sm" variant={f.byMonth ? "default" : "outline"} onClick={() => setF({ ...f, byMonth: !f.byMonth })} data-testid="history-side-by-side-toggle">
        <Columns3 className="w-4 h-4 mr-1" />Months side by side
      </Button>
    )}
  </div>
);

const Body = ({ f, d, rep, admin, cols, rows, metric, setMetric }) => {
  if (!rows.length && !d.months?.some((m) => m.rows.length)) return <p className="text-sm text-slate-500 py-6" data-testid="history-empty">No {rep.name} data for these dates and stores.</p>;
  if (f.report === "dlar") return <DealerReportTable columns={d.columns} rows={rows} />;
  if (f.report === "leaderboard") return <LeaderboardHistory d={d} admin={admin} byMonth={f.byMonth} metric={metric} setMetric={setMetric} />;
  return (
    <table className="vz-compact w-full" data-testid="history-table">
      <thead><tr className="border-b border-slate-200 text-left text-slate-500">
        {cols.map((c) => <th key={c.label} className={c.total ? "text-right" : ""}>{c.label}</th>)}
      </tr></thead>
      <tbody>{rows.map((r, i) => (
        <tr key={`${r.door_tsp}-${i}`} className="border-b border-slate-100" data-testid={`history-row-${r.door_tsp}`}>
          {cols.map((c) => <td key={c.label} className={`${c.total ? "text-right tabular-nums" : ""} ${c.wide ? "vz-wide" : ""}`} title={c.wide ? c.get(r) || "" : undefined}>{fmt(c.get(r), c)}</td>)}
        </tr>
      ))}</tbody>
      <TotalsRow rows={rows} cols={cols} label={`Total (${rows.length} stores)`} />
    </table>
  );
};

// One report's history for admins (all stores) or dealers (own stores only)
export const ReportHistory = ({ mode = "admin", report = "dlar" }) => {
  const admin = mode === "admin";
  const [stores, setStores] = useState([]);
  const [f, setF] = useState(() => { const [s, e] = PRESETS.this_month(); return { report, preset: "this_month", start: s, end: e, tsps: [], byMonth: false }; });
  const [metric, setMetric] = useState("overall");
  const [d, setD] = useState(null);
  useEffect(() => { axios.get(`${B}/api/verizon-sales/${mode}/history/stores`, auth()).then((r) => setStores(r.data)).catch(() => {}); }, [mode]);
  useEffect(() => {
    if (!f.start || !f.end) return undefined;
    const ctl = new AbortController();
    setD(null);
    axios.get(`${B}/api/verizon-sales/${mode}/history/${f.report}`, { ...auth(), signal: ctl.signal, params: { start: f.start, end: f.end, tsps: f.tsps.join(",") || undefined, by_month: f.byMonth || undefined } })
      .then((r) => setD(r.data))
      .catch((e) => { if (axios.isCancel(e)) return; toast.error(e.response?.data?.detail || "Couldn't load the report"); setD({ rows: [] }); });
    return () => ctl.abort();
  }, [f, mode]);
  const rep = HISTORY_REPORTS[f.report];
  const rows = d?.rows || [];
  const cols = f.report === "dlar" ? dlarCols(d) : f.report === "leaderboard" ? [] : [...STORE_COLS(admin), ...rep.cols];
  const exp = f.report === "leaderboard" && d ? leaderboardExport(d, admin, f.byMonth, metric) : { cols, rows };
  const shown = d?.months ? ` Showing ${d.months.length ? d.months.map((m) => m.month).join(", ") : "no months"}.`
    : d?.date ? ` Showing the report from ${d.date}${d.month ? ` (${d.month})` : ""}.` : d?.month ? ` Showing ${d.month}.` : "";
  return (
    <Card className="border-slate-200" data-testid={`report-history-${f.report}`}>
      <CardContent className="p-4 lg:p-6 space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="font-heading font-semibold text-navy text-lg flex items-center gap-2"><History className="w-5 h-5 text-teal" />{rep.name} History</h3>
            <p className="text-sm text-slate-500" data-testid="history-summary">{rep.hint}{shown}</p>
          </div>
          <ExportButton title={`${rep.name} ${f.start} to ${f.end}`} cols={exp.cols} rows={exp.rows} testId="history-export-btn" />
        </div>
        <Filters f={f} setF={setF} stores={stores} />
        {!d ? <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-teal" /></div>
          : <Body f={f} d={d} rep={rep} admin={admin} cols={cols} rows={rows} metric={metric} setMetric={setMetric} />}
      </CardContent>
    </Card>
  );
};
